import React from 'react';
import { compose } from 'redux';
import { ScreenName } from '@helpcenter/constants';
import {
    isAdvancedCustomizationEditPageEnabled,
    isAdvancedCustomizationEnabled,
    isIncreasedFlexibilityEnabled,
    isSlashOptionalInHelpCenterHomeRoute,
    isRemoveNonCriticalResourceFromViewHomePageRouteEnabled,
    isCustomPagesUiEnabled,
} from '@helpcenter/feature-flags';
import { FlexibleHomepageLoader } from '@helpcenter/flexible-homepage-loader';
import ViewHomepageResource, {
    searchComponentVirtualAgentResource,
    knowledgeCardsResource,
} from '@helpcenter/view-homepage';
import { withAsync } from '@helpcenter/with-async';
import { useIntl } from 'react-intl-next';
import { lazyForPaint } from 'react-loosely-lazy';
import type { PreloadedQuery } from 'react-relay';
import type { RouteResource, Route } from 'react-resource-router';
import { FlexibleEditpageLoader } from 'view/advanced-help-center/flexible-edit-page-loader';
import { NotFoundPage } from 'view/app/routes';
import { withOnlyJiraAdmin } from 'view/common/with-only-jira-admin';
import { withOnlyLoggedIn } from 'view/common/with-only-logged-in';
import { HelpCenter } from 'view/help-center';
import { SplashLayoutRoute } from 'view/layout';
import { PrefetchBrandingModel } from 'view/layout/prefetch-branding-model';
import { authPageMessage, callToActionUrlText } from 'view/portal-reorder-experience';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { withAnalyticsContext } from '@atlassian/analytics-web-react';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getIsHCCustomizationEnabled, getEnv } from '@atlassian/help-center-common-util/env';
import { createRouteWithLayout } from '@atlassian/help-center-common-util/resource-router';
import EditHomepageResource from './resources/edit-homepage';
import EditHomepageV2Resource from './resources/edit-homepage-v2';
import { brandingResource, featuredAndSortedPortalsResource, topicsResource } from './resources/multi-help-centers';

const VIEW_HOMEPAGE_PATH = isSlashOptionalInHelpCenterHomeRoute() ? '(/)?' : '/';
const EDIT_HOMEPAGE_PATH = '/edit';

const AsyncViewHomepage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-view-homepage" */ '../../view/advanced-help-center/view-homepage')
    ),
    LoadingSkeleton: () => <FlexibleHomepageLoader />,
});

const AsyncCSMViewHomepage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-csm-view-homepage" */ '../../view/customer-service/view-homepage')
    ),
    LoadingSkeleton: () => <FlexibleHomepageLoader />,
});

const getMultiHelpCenterRoute = () =>
    createRouteWithLayout(
        {
            name: 'help-center',
            path: '/',
            exact: true,
            component: HelpCenter,
            resources: __SERVER__
                ? [
                      brandingResource,
                      featuredAndSortedPortalsResource,
                      topicsResource,
                      searchComponentVirtualAgentResource,
                  ]
                : [searchComponentVirtualAgentResource],
        },
        ScreenName.HELP_CENTER,
        {
            paperAppearance: 'none',
            hideSearchButton: true,
            fadeInSearchModal: false,
        }
    );

export const getFlexiHelpCenterRoute = (
    name: string = 'help-center',
    path: string = VIEW_HOMEPAGE_PATH,
    screenName: ScreenName = ScreenName.VIEW_HOMEPAGE
) => {
    const viewHomePageSSRResources = isRemoveNonCriticalResourceFromViewHomePageRouteEnabled()
        ? [brandingResource, ViewHomepageResource, searchComponentVirtualAgentResource]
        : [
              brandingResource,
              featuredAndSortedPortalsResource,
              topicsResource,
              ViewHomepageResource,
              searchComponentVirtualAgentResource,
          ];

    return createRouteWithLayout(
        {
            name,
            path,
            exact: true,
            component: AsyncViewHomepage,
            /**
             * These resources can be removed once the individual elements data starts coming in the layout response
             */
            resources: __SERVER__
                ? viewHomePageSSRResources
                : [ViewHomepageResource, searchComponentVirtualAgentResource],
        },
        screenName,
        {
            paperAppearance: 'none',
            fadeInSearchModal: true,
            hideSearchButton: true,
            hideBanner: true,
            removeHeader: true,
            prefetchBrandingModel: true,
            hideSkipToMainContent: true,
        }
    );
};

export const getCSMFlexiHelpCenterRoute = (
    name: string = 'help-center',
    path: string = VIEW_HOMEPAGE_PATH,
    screenName: ScreenName = ScreenName.CSM_VIEW_HOMEPAGE
) => {
    // Upgrading @atlaskit/platform; fix these violations.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@atlaskit/platform/ensure-feature-flag-registration
    const KnowledgeCardsResource: RouteResource<PreloadedQuery<any>>[] = fg('jcs_knowledge_cards')
        ? [knowledgeCardsResource]
        : [];
    const viewHomePageSSRResources = [
        brandingResource,
        ViewHomepageResource,
        ...KnowledgeCardsResource,
        searchComponentVirtualAgentResource,
    ];

    return createRouteWithLayout(
        {
            name,
            path,
            exact: true,
            component: AsyncCSMViewHomepage,
            /**
             * These resources can be removed once the individual elements data starts coming in the layout response
             */
            resources: __SERVER__
                ? viewHomePageSSRResources
                : [ViewHomepageResource, ...KnowledgeCardsResource, searchComponentVirtualAgentResource],
        },
        screenName,
        {
            paperAppearance: 'none',
            fadeInSearchModal: true,
            hideSearchButton: true,
            hideBanner: true,
            removeHeader: true,
            prefetchBrandingModel: true,
        }
    );
};

export const getViewHomePageRoute = () => {
    if (isCSMHelpCenter(getEnv().helpCenterType)) {
        return getCSMFlexiHelpCenterRoute();
    }
    if (isAdvancedCustomizationEnabled()) return getFlexiHelpCenterRoute();
    return getMultiHelpCenterRoute();
};

interface Path {
    location: {
        pathname: string;
    };
}

export const AsyncEditHomepage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-edit-homepage" */ '../../view/advanced-help-center/edit-homepage')
    ),
    LoadingSkeleton: () =>
        isIncreasedFlexibilityEnabled() ? (
            <FlexibleEditpageLoader showActionBar />
        ) : (
            <FlexibleHomepageLoader showActionBar />
        ),
});

export const AsyncEditHomepageV2 = withAsync({
    loader: lazyForPaint(
        () =>
            import(/* webpackChunkName: "async-edit-homepage-v2" */ '../../view/advanced-help-center/edit-homepage-v2')
    ),
    LoadingSkeleton: () =>
        isIncreasedFlexibilityEnabled() ? (
            <FlexibleEditpageLoader showActionBar />
        ) : (
            <FlexibleHomepageLoader showActionBar />
        ),
});

export const EditHomePageLayout = (props: Path) => {
    const { formatMessage } = useIntl();
    const WrappedComponent = compose(
        withOnlyLoggedIn({
            redirectUrl: props.location.pathname,
        }),
        withOnlyJiraAdmin({
            message: formatMessage(authPageMessage),
            callToActionText: formatMessage(callToActionUrlText),
            callToActionUrl: '/',
        })
    )(() => (
        <SplashLayoutRoute
            path={EDIT_HOMEPAGE_PATH}
            component={isCustomPagesUiEnabled() ? AsyncEditHomepageV2 : AsyncEditHomepage}
            screenName={ScreenName.EDIT_HOMEPAGE}
            isFooterHidden={isIncreasedFlexibilityEnabled()}
        />
    ));

    return (
        <>
            <PrefetchBrandingModel />
            <WrappedComponent {...props} />
        </>
    );
};

export const getEditHomePageRoute = (name: string = 'edit-homepage', path: string = '/edit') => {
    const baseResources = [isCustomPagesUiEnabled() ? EditHomepageV2Resource : EditHomepageResource];

    const resources =
        // Upgrading @atlaskit/platform; fix these violations.
        // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-registration
        isCSMHelpCenter(getEnv().helpCenterType) && fg('jcs_knowledge_cards')
            ? [...baseResources, knowledgeCardsResource]
            : baseResources;

    const editHomePageRoute: Route = {
        name,
        path,
        resources,
        exact: true,
        component:
            isAdvancedCustomizationEnabled() &&
            getIsHCCustomizationEnabled() &&
            isAdvancedCustomizationEditPageEnabled()
                ? React.memo(EditHomePageLayout)
                : withAnalyticsContext<React.ComponentProps<typeof NotFoundPage>>({ componentName: 'notFoundPage' })(
                      withAnalyticsEvents({})(NotFoundPage)
                  ),
    };
    return editHomePageRoute;
};
